import {ActionProvider} from '@github-ui/code-view-shared/contexts/ActionContext'
import {CopilotSettingsProvider} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {EditPermissionsProvider} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {FileQueryProvider} from '@github-ui/code-view-shared/contexts/FileQueryContext'
import {PathProvider} from '@github-ui/code-view-shared/contexts/PathContext'
import {RefInfoProvider} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {useTreeList} from '@github-ui/code-view-shared/hooks/use-tree-list'
import type {DirectoryItem} from '@github-ui/code-view-types'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {makeFileReference, referenceID} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {type CopilotChatRepo, NullMessageId} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {FileTreePicker} from '@github-ui/copilot-spaces/components/FileTreePicker'
import {convertFlatPathsToTreeItems, type FlatPathsResponse} from '@github-ui/copilot-spaces/utils/convert-tree-data'
import {CurrentRepositoryProvider, type Repository} from '@github-ui/current-repository'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {TreeItem} from '@github-ui/repos-file-tree-view'
import {useLatestCommit} from '@github-ui/use-latest-commit'
import {useCallback, useEffect, useState} from 'react'

import type {File} from '../ContentPreview/content-preview-types'
import {useContentPreview} from '../ContentPreview/ContentPreviewContext'
import styles from './ResourcePanel.module.css'

export function FileTab({
  fetchError,
  loading,
  selectedBranch,
  selectedRepo,
}: {
  fetchError: string
  loading: boolean
  selectedBranch: string | undefined
  selectedRepo: CopilotChatRepo | null
}) {
  const isNonDefaultBranch = !!selectedBranch && selectedBranch !== selectedRepo?.defaultBranch
  const needsCommitFetch = isNonDefaultBranch || !selectedRepo?.commitOID
  const [commit, commitLoadingRaw, errorLoadingCommit] = useLatestCommit(
    selectedRepo?.ownerLogin,
    selectedRepo?.name,
    // Fetch the commit when on a non-default branch, or when the default branch
    // is missing a commitOID (e.g. the repo object was loaded without one).
    needsCommitFetch ? (selectedBranch ?? selectedRepo?.defaultBranch) : undefined,
    '/',
  )
  // useLatestCommit returns loading=true even when it's not actually loading anything because it's not given a branch name to look up
  const commitLoading = commitLoadingRaw && !!selectedRepo && needsCommitFetch
  const oid = needsCommitFetch ? commit?.oid : selectedRepo?.commitOID

  if (!selectedRepo) {
    return <div className={styles.resourcePanelPlaceholder}>Select a repository to view files</div>
  } else if (loading || commitLoading || !oid) {
    return <div className={styles.resourcePanelPlaceholder}>Loading repository...</div>
  } else {
    return (
      <div>
        <CurrentRepositoryProvider repository={selectedRepo as unknown as Repository}>
          <FileQueryProvider>
            <PathProvider path="test">
              <ActionProvider action="tree">
                <CopilotSettingsProvider copilotAccessAllowed>
                  <RefInfoProvider
                    refInfo={{
                      name: selectedBranch ?? selectedRepo.refInfo?.name ?? selectedRepo.defaultBranch,
                      canEdit: true,
                      listCacheKey: 'anything',
                      currentOid: oid,
                    }}
                  >
                    <EditPermissionsProvider canEdit canEditOnDefaultBranch={false} fileExistsOnDefault={false}>
                      <ResourcePanelFileTree
                        branch={selectedBranch || selectedRepo.defaultBranch}
                        currentRepo={selectedRepo}
                        fetchError={!!fetchError || !!errorLoadingCommit}
                        oid={oid}
                      />
                    </EditPermissionsProvider>
                  </RefInfoProvider>
                </CopilotSettingsProvider>
              </ActionProvider>
            </PathProvider>
          </FileQueryProvider>
        </CurrentRepositoryProvider>
      </div>
    )
  }
}

function ResourcePanelFileTree({
  branch,
  currentRepo,
  fetchError,
  oid,
}: {
  branch: string
  currentRepo: CopilotChatRepo
  fetchError: boolean
  oid: string
}) {
  const {
    openPreviewPane,
    openEphemeralItem,
    updateItem,
    selectedItem,
    items: previewItems,
    resourcePanelExpandedPath,
    setResourcePanelExpandedPath,
  } = useContentPreview()
  const manager = useChatManager()
  const {list, directories, loading} = useTreeList(oid, true)
  const [rootItems, setRootItems] = useState<Array<TreeItem<DirectoryItem>>>([])
  const [selectedItems, setSelectedItems] = useState<Set<string>>(() => new Set())

  const onSelectFile = useCallback(
    async (selectedFileItems: Set<string>) => {
      if (selectedFileItems.size === 0) return
      const selectedFilePath = Array.from(selectedFileItems)[0]!
      const fileContents = await manager.service.fetchFileContents(currentRepo.id, oid, selectedFilePath)
      if (fileContents.ok && fileContents.payload) {
        const ref = makeFileReference(selectedFilePath, currentRepo, branch, oid)
        const newFile: File = {
          messageId: NullMessageId,
          id: `file:${referenceID(ref)}#0`,
          timestamp: new Date(),
          isUserEdited: false,
          value: fileContents.payload?.content,
          type: 'file',
          name: selectedFilePath,
          isStreaming: false,
          language: fileContents.payload?.language ?? 'plaintext',
          reference: ref,
        }
        updateItem(newFile)
        openEphemeralItem(newFile.id)
        openPreviewPane()
        sendEvent('dotcom_chat.activate', {
          target: 'RESOURCE_PANEL_FILE_CLICK',
          mode: 'immersive',
          filePath: selectedFilePath,
        })
      }
      setSelectedItems(new Set([selectedFilePath]))
      setResourcePanelExpandedPath('')
    },
    [
      branch,
      currentRepo,
      manager.service,
      oid,
      openEphemeralItem,
      openPreviewPane,
      setResourcePanelExpandedPath,
      updateItem,
    ],
  )

  // Update tree items when list or directories change
  useEffect(() => {
    const response = {paths: list, directories} as FlatPathsResponse
    const treeItems = convertFlatPathsToTreeItems(response)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRootItems(treeItems)
  }, [list, directories, setRootItems])

  // Sync tree selection with active tab (only if the file belongs to the current repo)
  useEffect(() => {
    if (selectedItem) {
      const item = previewItems.get(selectedItem)
      if (
        item?.type === 'file' &&
        item.name &&
        item.reference &&
        'repoID' in item.reference &&
        item.reference.repoID === currentRepo.id
      ) {
        // Update selected items to highlight the file in the tree
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedItems(new Set([item.name]))
      } else {
        setSelectedItems(new Set())
      }
    }
  }, [selectedItem, previewItems, currentRepo.id])

  // Calculate expanded path: explicit directory override (from tree URL clicks) takes priority,
  // then falls back to the currently selected file's path in this repo.
  const expandedPath = (() => {
    if (resourcePanelExpandedPath) {
      return resourcePanelExpandedPath
    }
    if (selectedItem) {
      const item = previewItems.get(selectedItem)
      if (
        item?.type === 'file' &&
        item.name &&
        item.reference &&
        'repoID' in item.reference &&
        item.reference.repoID === currentRepo.id
      ) {
        return item.name
      }
    }
    return ''
  })()

  return (
    <FileTreePicker
      rootItems={rootItems}
      expandedPath={expandedPath}
      setRootItems={setRootItems}
      navigateOnClick={false}
      directoryNavigateOnClick={false}
      selectedItems={selectedItems}
      onSelectionChange={onSelectFile}
      loading={loading || false}
      fetchError={!!fetchError}
      sortDirectoryItems={treeItems => treeItems.sort((a, b) => a.data.name.localeCompare(b.data.name))}
      singleSelect
    />
  )
}
